/**
 * Pixel width of Lead Generation overlay lines in Outfit, the font the
 * creative card actually draws. A character cap is not enough: title-case
 * Spanish at the cap still runs off the 520px card.
 *
 * Advances are Outfit's raw glyph widths. The renderer paints about 8% wider
 * than those advances, so every width is scaled before it is compared to the
 * text column.
 */

const UPEM = 1000;
const RENDER_SLACK = 1.08;

/** 520px card, 40px padding, 34px check, 14px gap, 27px semibold. */
const BULLET_COLUMN_PX = 520 - 40 - 40 - 34 - 14;
/** Same card, subtitle has no check. 28px medium. */
const TITLE_COLUMN_PX = 520 - 40 - 40;

const BULLET_SIZE = 27;
const TITLE_SIZE = 28;

/** Outfit SemiBold advances for U+0020 through U+007E, units per em 1000. */
const SEMIBOLD_ASCII = [
  195, 274, 441, 652, 594, 671, 680, 246, 304, 304, 490, 555, 280, 462, 295, 393,
  660, 367, 553, 550, 604, 553, 568, 525, 555, 568, 281, 275, 555, 555, 555, 503,
  747, 715, 631, 686, 747, 602, 578, 778, 721, 280, 513, 688, 556, 850, 727, 800,
  616, 820, 640, 569, 629, 696, 705, 992, 707, 679, 598, 345, 393, 345, 459, 493,
  308, 588, 588, 492, 588, 541, 419, 579, 562, 248, 257, 528, 247, 867, 562, 569,
  588, 588, 434, 444, 389, 528, 523, 769, 523, 526, 466, 334, 291, 334, 555,
] as const;

/** Outfit Medium advances for U+0020 through U+007E. */
const MEDIUM_ASCII = [
  200, 262, 426, 641, 584, 658, 669, 242, 299, 299, 491, 547, 276, 461, 290, 401,
  655, 355, 541, 540, 599, 545, 562, 522, 548, 562, 271, 269, 547, 547, 547, 506,
  737, 702, 622, 683, 740, 590, 566, 772, 712, 267, 494, 672, 550, 842, 717, 794,
  604, 820, 628, 556, 619, 690, 696, 982, 698, 672, 589, 341, 401, 341, 451, 481,
  293, 580, 580, 487, 580, 534, 403, 575, 551, 234, 242, 511, 232, 861, 551, 563,
  580, 580, 425, 430, 378, 517, 510, 754, 509, 518, 451, 326, 282, 326, 547,
] as const;

const SEMIBOLD_EXTRA: Record<string, number> = {
  "—": 864,
  "–": 561,
  "¿": 486,
  "¡": 278,
  "«": 493,
  "»": 493,
};

const MEDIUM_EXTRA: Record<string, number> = {
  "—": 861,
  "–": 553,
  "¿": 483,
  "¡": 269,
  "«": 476,
  "»": 476,
};

const SEMIBOLD_FALLBACK = 850;
const MEDIUM_FALLBACK = 842;

function advance(
  char: string,
  table: readonly number[],
  extras: Record<string, number>,
  fallback: number,
): number {
  const extra = extras[char];
  if (extra != null) return extra;
  const folded = char.normalize("NFKD").replace(/\p{M}/gu, "");
  const code = folded.codePointAt(0);
  if (code != null && code >= 32 && code <= 126) {
    return table[code - 32] ?? fallback;
  }
  return fallback;
}

function textWidthPx(
  text: string,
  table: readonly number[],
  extras: Record<string, number>,
  fallback: number,
  size: number,
): number {
  let units = 0;
  for (const char of text) {
    units += advance(char, table, extras, fallback);
  }
  return (units * size * RENDER_SLACK) / UPEM;
}

export function leadGenAdOverlayBulletWidth(text: string): number {
  return textWidthPx(text, SEMIBOLD_ASCII, SEMIBOLD_EXTRA, SEMIBOLD_FALLBACK, BULLET_SIZE);
}

export function leadGenAdOverlayTitleWidth(text: string): number {
  return textWidthPx(text, MEDIUM_ASCII, MEDIUM_EXTRA, MEDIUM_FALLBACK, TITLE_SIZE);
}

/** Licence line on the card footer, Outfit SemiBold at the given size. */
export function leadGenAdLicenseWidth(text: string, fontSize: number): number {
  return textWidthPx(text, SEMIBOLD_ASCII, SEMIBOLD_EXTRA, SEMIBOLD_FALLBACK, fontSize);
}

/**
 * Footer row: the licence keeps its full width. The logo fills what is left.
 * With no licence, the logo can use the whole row.
 */
export function leadAdFooterLayout(input: {
  cardInnerWidth: number;
  licenseText?: string | null;
  licenseFontSize: number;
  gap?: number;
  dividerWidth?: number;
}): { logoMaxWidth: number; licenseWidth: number } {
  const gap = input.gap ?? 14;
  const dividerWidth = input.dividerWidth ?? 1;
  const license = input.licenseText?.trim();
  if (!license) {
    return { logoMaxWidth: input.cardInnerWidth, licenseWidth: 0 };
  }
  const licenseWidth = Math.ceil(leadGenAdLicenseWidth(license, input.licenseFontSize));
  const reserved = licenseWidth + gap + dividerWidth;
  const logoMaxWidth = Math.max(0, input.cardInnerWidth - reserved);
  return { logoMaxWidth, licenseWidth };
}

export function leadGenAdOverlayBulletFits(text: string): boolean {
  return leadGenAdOverlayBulletWidth(text) <= BULLET_COLUMN_PX;
}

export function leadGenAdOverlayTitleFits(text: string): boolean {
  return leadGenAdOverlayTitleWidth(text) <= TITLE_COLUMN_PX;
}

/**
 * Character budget for a line that is already too wide. The rewriter is told
 * this count so it cannot return another line of the same length.
 */
export function leadGenAdTextCharBudget(
  text: string,
  widthPx: number,
  maxWidthPx: number,
  hardMax: number,
): number {
  if (widthPx <= maxWidthPx) return hardMax;
  const scaled = Math.floor((text.length * maxWidthPx) / widthPx);
  return Math.max(8, Math.min(hardMax, scaled, Math.max(8, text.length - 1)));
}

export function leadGenAdOverlayBulletCharBudget(text: string, hardMax: number): number {
  return leadGenAdTextCharBudget(
    text,
    leadGenAdOverlayBulletWidth(text),
    BULLET_COLUMN_PX,
    hardMax,
  );
}

export function leadGenAdOverlayTitleCharBudget(text: string, hardMax: number): number {
  return leadGenAdTextCharBudget(
    text,
    leadGenAdOverlayTitleWidth(text),
    TITLE_COLUMN_PX,
    hardMax,
  );
}

const TRAILING_PUNCTUATION = /[\s.,;:–—-]+$/u;

/** Drop whole words, then characters, until the line fits the card. */
export function clipLeadGenAdLineToWidth(
  value: string,
  fits: (text: string) => boolean,
): string {
  let text = value.trim();
  while (text.length > 2 && !fits(text)) {
    const space = text.lastIndexOf(" ");
    const candidate = (space > 0 ? text.slice(0, space) : text.slice(0, -1))
      .replace(TRAILING_PUNCTUATION, "")
      .trim();
    if (candidate.length >= text.length) break;
    text = candidate.length > 0 ? candidate : text.slice(0, -1).trim();
  }
  return text;
}
